import * as React from 'react';
import { useEffect } from 'react';
import { generatePath, Navigate, Route, Routes as RoutesSwitch } from 'react-router-dom';
import { Routes, useJourneyNavigation } from '../../routes';
import { useOrgStore } from '../../hooks/useOrgStore';
import { LoaderWidget } from '../../widgets/LoaderWidget';
import { observer } from 'mobx-react';
import { useSystem } from '../../hooks/useSystem';
import { AppsPage } from './manage-apps/AppsPage';
import { NotFound } from '../NotFound';
import { UsersPage } from './manage-users/UsersPage';
import { IntegrationsPage } from './manage-integrations/IntegrationsPage';
import { styled } from '../../theme';
import { ENV } from '../../env';
import { SubscriptionsPage } from './manage-subscriptions/SubscriptionsPage';
import { CardinalModel, OrganizationModelAction, PlanModelAction } from '@journeyapps-platform/cardinal-catalog';
import { SettingsPage } from './settings/SettingsPage';
import { BillingPage } from './manage-billing/BillingPage';
import { SupportPage } from './support/SupportPage';
import { SupportTicketPage } from './support/SupportTicketPage';

export interface AdminLandingProps {}

export const PAGE_TITLE = 'JourneyApps | Admin Portal';
const STORAGE_KEY = 'admin-portal-last-org';

function loadLastOrg() {
  return localStorage.getItem(STORAGE_KEY);
}

export interface OrgSidebarItem {
  label: string;
  path: Routes;
  authorization?: {
    model: CardinalModel;
    action: string;
  };
}

export const ADMIN_SIDEBAR_ITEMS: OrgSidebarItem[] = [
  {
    label: 'Projects',
    path: Routes.MANAGE_APPS
  },
  {
    label: 'Developers',
    path: Routes.MANAGE_USERS
  },
  {
    label: 'Integrations',
    path: Routes.MANAGE_INTEGRATIONS,
    authorization: {
      model: CardinalModel.INTEGRATION,
      action: PlanModelAction.READ
    }
  },
  {
    label: 'Settings',
    path: Routes.SETTINGS,
    authorization: {
      model: CardinalModel.ORG,
      action: OrganizationModelAction.UPDATE
    }
  },
  {
    label: 'Billing',
    path: Routes.BILLING,
    authorization: {
      model: CardinalModel.ORG,
      action: OrganizationModelAction.UPDATE_BILLING
    }
  },
  {
    label: 'Subscriptions',
    path: Routes.MANAGE_PLANS,
    authorization: {
      model: CardinalModel.PLAN,
      action: PlanModelAction.READ
    }
  },
  {
    label: 'Support',
    path: Routes.SUPPORT
  }
];

const AdminLanding: React.FC<AdminLandingProps> = observer(() => {
  const orgStore = useOrgStore();
  const system = useSystem();
  const { relativeRoute, navigate, params } = useJourneyNavigation(Routes.ADMIN_HOME);

  useEffect(() => {
    // Future work: Create a wrapping component that we can using in bodyWidget
    document.title = PAGE_TITLE;
  }, []);

  useEffect(() => {
    if (params.org_id) {
      orgStore
        .changeOrganization(params.org_id!)
        .then((success) => {
          if (!success) {
            localStorage.removeItem(STORAGE_KEY);
            navigate(Routes.MANAGE_APPS, params);
          } else {
            localStorage.setItem(STORAGE_KEY, params.org_id!);
          }
        })
        .catch((err) => {
          console.error(`Failed to load organization (org_id: ${params.org_id})`, err);
          localStorage.removeItem(STORAGE_KEY);
        });
    } else {
      const lastOrg = loadLastOrg();
      if (lastOrg) {
        navigate(Routes.ADMIN_HOME, {
          org_id: lastOrg
        });
        return;
      }
      navigate(Routes.ADMIN_HOME, {
        org_id: ENV.USER_ORG
      });
    }
  }, [params.org_id]);

  if (!orgStore.current_org) {
    return <LoaderWidget />;
  }

  return (
    <S.Container>
      <RoutesSwitch>
        <Route
          path={relativeRoute(Routes.ADMIN_HOME)}
          element={<Navigate to={generatePath(Routes.MANAGE_APPS, params as any)} />}
        />
        <Route path={relativeRoute(Routes.MANAGE_APPS)} element={<AppsPage />} />
        <Route path={relativeRoute(Routes.MANAGE_USERS)} element={<UsersPage />} />
        <Route path={relativeRoute(Routes.MANAGE_PLANS)} element={<SubscriptionsPage />} />
        <Route path={relativeRoute(Routes.MANAGE_INTEGRATIONS)} element={<IntegrationsPage />} />
        <Route path={relativeRoute(Routes.SETTINGS)} element={<SettingsPage />} />
        <Route path={relativeRoute(Routes.SUPPORT)} element={<SupportPage />} />
        <Route path={relativeRoute(Routes.SUPPORT_TICKET)} element={<SupportTicketPage />} />
        <Route path={relativeRoute(Routes.BILLING)} element={<BillingPage />} />
        {/* To be enabled in future when users can create their own roles */}
        {/* <Route path={relativeRoute(Routes.MANAGE_ROLES)} element={<ManageRolesPage />} /> */}
        <Route element={<NotFound backPath={Routes.ADMIN_HOME} />} />
      </RoutesSwitch>
    </S.Container>
  );
});

namespace S {
  export const Container = styled.div`
    height: 100%;
    width: 100%;
    position: relative;
  `;
}
export const AdminLandingPage = AdminLanding;
